import { useBuildPlan } from '@/hooks/lesson/useBuildPlan';
import { useBuildPreview } from '@/hooks/lesson/useBuildPreview';
import type { StepView } from '@/types/pipeline';
import { isLateBuildStage, type BuildPanelKind } from './buildStages';
import { BuildOutline, useDraftOutline } from './BuildOutline';
import { BuildPlanCard } from './BuildPlanCard';
import { BuildPreview } from './BuildPreview';
import { BuildWaitCards } from './BuildWaitCards';

interface BuildPanelProps {
  lessonId?: string;
  /** Which content the active step asks for (`deriveBuildStages`). */
  panel: BuildPanelKind;
  /** Source ingestion has completed: there is something read to preview. */
  sourcesDone: boolean;
  byteSelection?: StepView;
  /** The student's own files vs sources found for a topic. */
  hasUserSources?: boolean;
}

/**
 * What sits under the active step, each sized to itself (never stretched to
 * fill the panel, never scrolled):
 * - reading / gathering → one tip card (`BuildWaitCards`)
 * - planning → "Found in your material" with a one-line tip beneath, or just
 *   the tip card when nothing was found
 * - building → the plan as the outline placed it (`BuildPlanCard`: summary,
 *   sections, concepts, what was set aside), else the draft mind map, else
 *   the material while neither has landed yet
 */
export function BuildPanel({
  lessonId,
  panel,
  sourcesDone,
  byteSelection,
  hasUserSources,
}: BuildPanelProps) {
  const preview = useBuildPreview(lessonId, { enabled: sourcesDone });
  const tree = useDraftOutline(lessonId, {
    enabled: panel === 'outline',
    polling: panel === 'outline' && isLateBuildStage(byteSelection),
  });
  const plan = useBuildPlan(lessonId, { enabled: panel === 'outline' });
  const previewSources = preview.data?.sources ?? [];

  if (panel === 'outline' && plan) return <BuildPlanCard plan={plan} />;
  if (panel === 'outline' && tree) return <BuildOutline tree={tree} />;
  if (panel !== 'cards' && previewSources.length > 0) {
    return (
      <div className='flex max-h-full w-full flex-col gap-5 overflow-hidden rounded-xl border border-border bg-card p-5 shadow-sm'>
        <BuildPreview
          sources={previewSources}
          hasUserSources={hasUserSources}
          className='min-h-0'
        />
        <BuildWaitCards lessonId={lessonId} compact />
      </div>
    );
  }
  return (
    <div className='flex w-full justify-center'>
      <BuildWaitCards lessonId={lessonId} />
    </div>
  );
}
